/**
 * Story 054 — Country autocomplete + "Lookup from coords" button.
 *
 * - Static ISO 3166-1 alpha-2 list (see `iso3166.ts`).
 * - "Lookup from coords" fires `GET /api/assets/v1/geocode/country-from-coords`
 *   ONLY on explicit button click — never on lat/lon change (per
 *   spec 056 + UX brief §5.3, avoids burning Nominatim quota on every
 *   keystroke).
 * - Button is disabled until both lat and lon are valid numbers in
 *   range.
 *
 * R58-W-02 (2026-05-19) — switched from ISO 3166-1 alpha-3 to
 * alpha-2 to align with the on-master contract `assets/v1@4.2.0`,
 * which narrowed `Site.countryCode` / `CreateSiteBody.countryCode`
 * to `pattern: "^[A-Z]{2}$"`. Alpha-3 inputs (legacy form state)
 * still parse via `toAlpha2` at submit time as a defensive
 * passthrough.
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import { ISO_3166_ALPHA2, toAlpha3, type CountryOption } from "./iso3166.js";
import { matchesCountrySearch } from "./countrySearchIndex.js";
import { useGeocodeMutation } from "../queries/index.js";
import { LocateFixedIcon } from "@aeriosiq/ui/icons";

export interface CountryAutocompleteProps {
  value: string | null;
  onChange: (next: string | null) => void;
  /** Coordinates that drive the optional "Lookup from coords" button.
   *  When BOTH are omitted (or `null`) the button is hidden entirely —
   *  surfaces with no lat/lon (e.g. the Create-Project park row, FB-05)
   *  render a plain country autocomplete. */
  latitude?: number | null;
  longitude?: number | null;
  readOnly?: boolean;
  /** Optional override label (defaults to "Country"). */
  label?: string;
  /** When true, surfaces a `*` via the underlying TextField. */
  required?: boolean;
  /** Error helper text; sets the field's error state when non-null. */
  errorText?: string | null;
  /** Optional callback for parent forms that want to know when the
   *  Lookup button programmatically filled the value (e.g. to mark the
   *  RHF form dirty). */
  onLookupSuccess?: (countryCode: string) => void;
  onLookupError?: (message: string) => void;
}

function coordsValid(
  lat: number | null,
  lon: number | null,
): lat is number & { __valid: true } {
  if (lat === null || lon === null) return false;
  if (Number.isNaN(lat) || Number.isNaN(lon)) return false;
  if (lat < -90 || lat > 90) return false;
  if (lon < -180 || lon > 180) return false;
  return true;
}

export function CountryAutocomplete({
  value,
  onChange,
  latitude = null,
  longitude = null,
  readOnly = false,
  label = "Country",
  required = false,
  errorText = null,
  onLookupSuccess,
  onLookupError,
}: CountryAutocompleteProps): ReactElement {
  const selected: CountryOption | null = value
    ? (ISO_3166_ALPHA2.find((c) => c.code === value) ?? {
        code: value,
        name: value,
      })
    : null;

  const geocode = useGeocodeMutation();
  const [lookupReady, setLookupReady] = useState(false);

  // Hide the geocode affordance entirely when the host gave us no
  // coordinates at all (FB-05 park row). A surface that wants the button
  // passes lat/lon as before.
  const hasCoords = latitude !== null || longitude !== null;
  const canLookup = coordsValid(latitude, longitude) && !readOnly;

  const filterOptions = useMemo(
    () =>
      (
        options: CountryOption[],
        state: { inputValue: string },
      ): CountryOption[] => {
        const q = state.inputValue.trim();
        if (q.length === 0) return options;
        return options.filter((opt) =>
          matchesCountrySearch(
            {
              code: opt.code,
              alpha2: opt.code,
              alpha3: toAlpha3(opt.code),
              englishName: opt.name,
            },
            q,
          ),
        );
      },
    [],
  );

  async function handleLookup(): Promise<void> {
    if (latitude === null || longitude === null) return;
    try {
      const result = await geocode.mutateAsync({
        lat: latitude,
        lon: longitude,
      });
      if (result.countryCode) {
        onChange(result.countryCode);
        onLookupSuccess?.(result.countryCode);
        setLookupReady(true);
        window.setTimeout(() => {
          setLookupReady(false);
        }, 2000);
      } else {
        onLookupError?.(
          "Couldn't determine a country from those coordinates. Enter it manually.",
        );
      }
    } catch {
      onLookupError?.(
        "Couldn't reach the geocoder. Try again or enter the country manually.",
      );
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Autocomplete<CountryOption>
        options={ISO_3166_ALPHA2}
        value={selected}
        readOnly={readOnly}
        filterOptions={filterOptions}
        onChange={(_e, next) => {
          onChange(next?.code ?? null);
        }}
        getOptionLabel={(opt) => opt.code}
        renderOption={(props, opt) => (
          <li {...props} key={opt.code}>
            <Box component="span" sx={{ fontFamily: "monospace", mr: 1 }}>
              {opt.code}
            </Box>
            <Box component="span" sx={{ color: "text.secondary" }}>
              — {opt.name}
            </Box>
          </li>
        )}
        isOptionEqualToValue={(o, v) => o.code === v.code}
        renderInput={(params) => {
          // exactOptionalPropertyTypes friction: MUI's Autocomplete
          // renderInput params carries undefined-bearing InputLabelProps
          // that TextField rejects under strict optionals. Spread via
          // an explicit-any cast to avoid littering ten optional fields.
          /* eslint-disable @typescript-eslint/no-explicit-any */
          const passThrough: any = params;
          return (
            <TextField
              {...passThrough}
              size="small"
              label={label}
              required={required}
              error={errorText !== null}
              helperText={errorText ?? undefined}
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  "aria-label": label,
                  ...(readOnly ? { readOnly: true } : {}),
                },
              }}
            />
          );
          /* eslint-enable */
        }}
        size="small"
      />
      {hasCoords ? (
        <Tooltip
          title={
            canLookup
              ? ""
              : "Enter latitude and longitude to look up the country."
          }
          placement="bottom-start"
        >
          <span>
            <Button
              variant="text"
              size="small"
              disabled={!canLookup || geocode.isPending}
              onClick={() => {
                void handleLookup();
              }}
              startIcon={
                geocode.isPending ? (
                  <CircularProgress size={14} />
                ) : (
                  <LocateFixedIcon fontSize="small" />
                )
              }
              aria-describedby={!canLookup ? "lookup-coords-help" : undefined}
              sx={{ alignSelf: "flex-start" }}
            >
              {geocode.isPending
                ? "Looking up…"
                : lookupReady
                  ? "Country looked up"
                  : "Lookup from coords"}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </Box>
  );
}
